import React from 'react';
import { Building2, Check, Loader2, Save, X } from 'lucide-react';
import { InlineMicButton } from '../InlineMicButton';
import { CodeSelector, CodeItem } from '../CodeSelector';
import type { Hospital, ProcedureTemplate, SuggestedPrescription } from '../../types/clinical';

export interface SOAPNoteEditorProps {
  // Vital signs
  bp: string;
  setBp: (v: string) => void;
  weight: string;
  setWeight: (v: string) => void;
  // Location
  hospitalId: string;
  setHospitalId: (v: string) => void;
  hospitals: Hospital[];
  // SOAP fields
  subjective: string;
  setSubjective: React.Dispatch<React.SetStateAction<string>>;
  objective: string;
  setObjective: React.Dispatch<React.SetStateAction<string>>;
  assessment: string;
  setAssessment: React.Dispatch<React.SetStateAction<string>>;
  plan: string;
  setPlan: React.Dispatch<React.SetStateAction<string>>;
  // Summary + Follow-up
  summary: string;
  setSummary: React.Dispatch<React.SetStateAction<string>>;
  followUp: string;
  setFollowUp: React.Dispatch<React.SetStateAction<string>>;
  // Templates
  templates: ProcedureTemplate[];
  // NHRPL
  nhrplCodes: string[];
  setNhrplCodes: React.Dispatch<React.SetStateAction<string[]>>;
  searchNhrpl: (query: string) => CodeItem[];
  nhrplLoading: boolean;
  // ICD-10
  icd10Suggested: string[];
  icd10Confirmed: string[];
  setIcd10Confirmed: React.Dispatch<React.SetStateAction<string[]>>;
  searchIcd10: (query: string) => CodeItem[];
  icd10Loading: boolean;
  // Suggested prescriptions
  suggestedPrescriptions: SuggestedPrescription[];
  setSuggestedPrescriptions: React.Dispatch<React.SetStateAction<SuggestedPrescription[]>>;
  onAcceptPrescription: (rx: SuggestedPrescription) => void;
  // Actions
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export function SOAPNoteEditor({
  bp, setBp, weight, setWeight,
  hospitalId, setHospitalId, hospitals,
  subjective, setSubjective, objective, setObjective, assessment, setAssessment, plan, setPlan,
  summary, setSummary, followUp, setFollowUp,
  templates,
  nhrplCodes, setNhrplCodes, searchNhrpl, nhrplLoading,
  icd10Suggested, icd10Confirmed, setIcd10Confirmed,
  searchIcd10, icd10Loading,
  suggestedPrescriptions, setSuggestedPrescriptions, onAcceptPrescription,
  saving, onSave, onCancel,
}: SOAPNoteEditorProps) {
  return (
    <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">SOAP Note</h2>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Consultation template selector */}
      {templates.filter(t => t.templateType === 'consultation').length > 0 && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">Load from Template</label>
          <select
            value=""
            onChange={e => {
              const tmpl = templates.find(t => t.id === e.target.value);
              if (tmpl) {
                if (tmpl.assessment) setAssessment(tmpl.assessment);
                if (tmpl.plan) setPlan(tmpl.plan);
                if (tmpl.summary) setSummary(tmpl.summary);
                if (tmpl.followUp) setFollowUp(tmpl.followUp);
                if (tmpl.defaultIcd10Codes?.length) {
                  setIcd10Confirmed(prev => Array.from(new Set([...prev, ...tmpl.defaultIcd10Codes])));
                }
                if (tmpl.defaultNhrplCodes?.length) {
                  setNhrplCodes(prev => Array.from(new Set([...prev, ...tmpl.defaultNhrplCodes])));
                }
              }
            }}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select a consultation template —</option>
            {templates.filter(t => t.templateType === 'consultation').map(t => (
              <option key={t.id} value={t.id}>{t.name}{t.category ? ` (${t.category})` : ''}</option>
            ))}
          </select>
        </div>
      )}

      {/* Vital Signs + Location */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">BP</label>
          <input value={bp} onChange={e => setBp(e.target.value)} placeholder="120/80" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Weight (kg)</label>
          <input value={weight} onChange={e => setWeight(e.target.value)} placeholder="65" className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center gap-1"><Building2 className="h-3 w-3" /> Location</label>
          <select
            value={hospitalId}
            onChange={e => setHospitalId(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-white"
          >
            <option value="">— Select —</option>
            {hospitals.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
      </div>

      {/* SOAP fields — S, O, A */}
      {[
        { label: 'Subjective', value: subjective, set: setSubjective, placeholder: 'Patient-reported symptoms and concerns...' },
        { label: 'Objective', value: objective, set: setObjective, placeholder: 'Examination findings, vitals, test results...' },
        { label: 'Assessment', value: assessment, set: setAssessment, placeholder: 'Clinical assessment and diagnosis...' },
      ].map(f => (
        <div key={f.label} className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-semibold text-gray-700">{f.label}</label>
            <InlineMicButton onTranscript={t => f.set((prev: string) => prev ? prev + ' ' + t : t)} />
          </div>
          <textarea
            value={f.value}
            onChange={e => f.set(e.target.value)}
            placeholder={f.placeholder}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      ))}

      {/* Plan */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <label className="text-sm font-semibold text-gray-700">Plan</label>
          <InlineMicButton onTranscript={t => setPlan(prev => prev ? prev + ' ' + t : t)} />
        </div>
        <textarea
          value={plan}
          onChange={e => setPlan(e.target.value)}
          placeholder="Treatment plan, medications, follow-up..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* ICD-10 Code Selector — pre-populated with AI suggestions, doctor can add/remove */}
      <div className="mb-4">
        {icd10Suggested.length > 0 && (
          <div className="mb-2">
            <label className="block text-xs text-gray-500 mb-1">AI Suggested (click to toggle)</label>
            <div className="flex flex-wrap gap-2">
              {icd10Suggested.map(code => {
                const confirmed = icd10Confirmed.includes(code);
                return (
                  <button
                    key={code}
                    onClick={() => {
                      if (confirmed) setIcd10Confirmed(prev => prev.filter(c => c !== code));
                      else setIcd10Confirmed(prev => [...prev, code]);
                    }}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      confirmed
                        ? 'bg-blue-100 border-blue-300 text-blue-800'
                        : 'bg-gray-100 border-gray-300 text-gray-600 line-through opacity-60 hover:bg-gray-200'
                    }`}
                  >
                    {confirmed && <Check className="h-3 w-3 inline mr-1" />}
                    {code}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <CodeSelector
          codeType="icd10"
          selected={icd10Confirmed.map(c => ({ code: c, description: '' }))}
          onChange={items => setIcd10Confirmed(items.map(i => i.code))}
          searchFn={searchIcd10}
          cacheLoading={icd10Loading}
          label="ICD-10 Codes"
          placeholder="Add more ICD-10 codes..."
        />
      </div>

      {/* NHRPL Procedure Codes */}
      <div className="mb-4">
        <CodeSelector
          codeType="nhrpl"
          selected={nhrplCodes.map(c => ({ code: c, description: '' }))}
          onChange={items => setNhrplCodes(items.map(i => i.code))}
          searchFn={searchNhrpl}
          cacheLoading={nhrplLoading}
          label="NHRPL Procedure Codes"
          placeholder="Add procedure codes..."
        />
      </div>

      {/* Summary + Follow-up */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-semibold text-gray-700">Summary</label>
            <InlineMicButton onTranscript={t => setSummary(prev => prev ? prev + ' ' + t : t)} />
          </div>
          <textarea
            value={summary}
            onChange={e => setSummary(e.target.value)}
            placeholder="Brief consultation summary..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-semibold text-gray-700">Follow-up</label>
            <InlineMicButton onTranscript={t => setFollowUp(prev => prev ? prev + ' ' + t : t)} />
          </div>
          <textarea
            value={followUp}
            onChange={e => setFollowUp(e.target.value)}
            placeholder="Follow-up plan, next appointment..."
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Suggested Prescriptions — Action Cards */}
      {suggestedPrescriptions.length > 0 && (
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Suggested Prescriptions</label>
          <div className="space-y-2">
            {suggestedPrescriptions.map(rx => (
              <div key={rx.drugName} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
                <div>
                  <span className="text-sm font-medium text-gray-900">{rx.drugName} {rx.dose}</span>
                  <span className="text-sm text-gray-500 ml-2">{rx.frequency} for {rx.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onAcceptPrescription(rx)}
                    className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => setSuggestedPrescriptions(prev => prev.filter(p => p.drugName !== rx.drugName))}
                    className="p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mt-6">
        <button
          onClick={onSave}
          disabled={saving || !subjective || !assessment}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Note
        </button>
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
